// Re-export RTK Query hooks with cleaner names
export {
  useGetAppointmentDetailsQuery,
  useGetEncounterDetailsQuery,
  useGetNoteDetailsQuery,
  useGetOrganizationProvidersQuery,
  useGetProviderAppointmentsQuery,
  useGetProviderByIdQuery,
  useGetProviderEncountersQuery,
  useGetProviderInfoQuery,
  useGetProviderNotesQuery,
  useGetProviderQuery,
  useGetProviderTranscriptionsQuery,
  useGetTranscriptDetailsQuery,
  useGetUserDetailsQuery,
  useSearchProvidersQuery,
  useGetFeaturesListQuery,
  useUpdateProviderFeatureFlagMutation,
} from './api';

// Import admin and organizations API from generated code
import { enhancedApi as adminApi } from '@store/generated/admin';
import { enhancedApi as organizationsApi } from '@store/generated/organizations';

// Re-export data-migration hooks from admin API object
export const {
  useBulkUpdatePatientStatusMutation,
  useDeleteImportSessionMutation,
  useExportImportResultsQuery,
  useGetImportOperationStatusQuery,
  useGetImportSessionStatusQuery,
  useGetMatchedPatientsQuery,
  useImportSelectedPatientsMutation,
  useImportUnmatchedPatientsMutation,
  useListImportOperationsQuery,
  useListOrganizationImportSessionsQuery,
  useMergeMultiplePatientsMutation,
  useRejectPatientsMutation,
} = adminApi;

// Re-export uploadImportFile from our custom implementation
export { useUploadImportFileMutation } from './api';

// Re-export Prospects (Sales Data) hooks
export {
  useGetProspectsQuery,
  useGetProspectByIdQuery,
  useUploadCsvProspectsMutation,
} from './api';

// Re-export organizations API hooks with user-friendly names
export const {
  useGetV2AdminOrganizationsQuery,
  useGetV2AdminOrganizationsByOrganizationIdQuery,
  usePatchV2AdminOrganizationsByOrganizationIdMutation,
  useGetV2AdminOrganizationsByOrganizationIdProvidersQuery,
  usePostV2AdminOrganizationsByOrganizationIdMembersMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdMembersAndUserIdMutation,
  usePostV2AdminOrganizationsByOrganizationIdMembersAndUserIdRolesMutation,
  usePostV2AdminOrganizationsByOrganizationIdMembersAndUserIdReactivateMutation,
  usePatchV2AdminOrganizationsByOrganizationIdMembersAndUserIdRolesMutation,
  usePostV2AdminOrganizationsByOrganizationIdSyncSubscriptionMutation,
  useGetV2AdminOrganizationsByOrganizationIdSubscriptionDashboardUrlQuery,
  usePostV2AdminOrganizationsByOrganizationIdExtendTrialMutation,
  usePostV2AdminOrganizationsByOrganizationIdCreateSubscriptionMutation,
  usePostV2AdminOrganizationsByOrganizationIdMergeMutation,
  // Templates and Forms
  useGetV2AdminOrganizationsByOrganizationIdTemplatesQuery,
  usePostV2AdminOrganizationsByOrganizationIdTemplatesMutation,
  usePutV2AdminOrganizationsByOrganizationIdTemplatesAndTemplateIdMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdTemplatesAndTemplateIdMutation,
  useGetV2AdminOrganizationsByOrganizationIdFormsQuery,
  usePostV2AdminOrganizationsByOrganizationIdFormsMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdFormsAndFormIdMutation,
  // Patient Visibility
  useGetV2AdminOrganizationsByOrganizationIdPatientVisibilityQuery,
  usePatchV2AdminOrganizationsByOrganizationIdPatientVisibilityMutation,
  // Data Security Admin
  useGetV2AdminOrganizationsByOrganizationIdDataSecurityQuery,
  usePutV2AdminOrganizationsByOrganizationIdBaaMutation,
  usePutV2AdminOrganizationsByOrganizationIdCoiMutation,
  usePutV2AdminOrganizationsByOrganizationIdDataRetentionMutation,
} = organizationsApi;

// Re-export admin template discovery API hooks with user-friendly names
export const {
  useGetV2AdminTemplatesUserTemplatesQuery: useGetUserTemplatesQuery,
  // Capabilities hooks (read-only - mutations removed with backend endpoints)
  useGetV2AdminCapabilitiesQuery: useGetCapabilitiesQuery,
  useGetV2AdminCapabilitiesByCapabilityIdQuery: useGetCapabilityQuery,
  // Bundles hooks (read-only - mutations removed with backend endpoints)
  useGetV2AdminBundlesQuery: useGetBundlesQuery,
  useGetV2AdminBundlesByBundleIdQuery: useGetBundleQuery,
  // Bundle assignments hooks
  useGetV2AdminBundleAssignmentsQuery: useGetBundleAssignmentsQuery,
  usePostV2AdminBundleAssignmentsMutation: useCreateBundleAssignmentMutation,
  useGetV2AdminBundleAssignmentsByAssignmentIdQuery: useGetBundleAssignmentQuery,
  usePutV2AdminBundleAssignmentsByAssignmentIdStatusMutation: useUpdateBundleAssignmentStatusMutation,
  // Entitlements hooks
  useGetV2AdminEntitlementsQuery: useGetEntitlementsQuery,
  useGetV2AdminEntitlementsUsageQuery: useGetEntitlementsUsageQuery,
} = adminApi;

// Re-export admin build hooks with user-friendly names
export const {
  usePostV2AdminNotetemplatesBuildFromTemplateMutation: useAdminBuildFromTemplateMutation,
  useGetV2AdminNotetemplatesBuildByTaskIdStatusQuery: useAdminBuildStatusQuery,
  usePostV2AdminNotetemplatesBuildByTaskIdSaveMutation: useAdminBuildSaveMutation,
} = adminApi;

// Import templates API for model conversion hooks
import { enhancedApi as templatesApi } from '@store/generated/templates';

// Re-export model conversion hooks with user-friendly names
export const {
  usePostV2AdminNotetemplatesModelConversionStartMutation: useStartModelConversionMutation,
  useGetV2AdminNotetemplatesModelConversionStatusByThreadIdQuery: useModelConversionStatusQuery,
  usePostV2AdminNotetemplatesModelConversionSaveMutation: useSaveModelConversionMutation,
  usePostV2AdminNotetemplatesModelConversionCopyToNewMutation: useCopyModelConversionMutation,
} = templatesApi;

// Re-export admin note templates API hooks
export {
  useGetAdminNoteTemplatesQuery,
  useLazyGetAdminNoteTemplatesQuery,
  useGetAdminUserNoteTemplatesQuery,
  useLazyGetAdminUserNoteTemplatesQuery,
  useGetAdminOrgNoteTemplatesQuery,
  useLazyGetAdminOrgNoteTemplatesQuery,
  useCreateAdminOrgNoteTemplateMutation,
  useCopyAdminNoteTemplateMutation,
  useConvertAdminNoteTemplateToOrgMutation,
  useDeleteAdminNoteTemplateMutation,
  // Single template GET/PUT hooks
  useGetAdminNoteTemplateQuery,
  useLazyGetAdminNoteTemplateQuery,
  useUpdateAdminNoteTemplateMutation,
  // Fork template for user hook (available after codegen)
  useForkTemplateForUserMutation,
  // Provider templates hook (what provider actually sees)
  useGetAdminProviderTemplatesQuery,
  // Revision history hooks
  useGetAdminTemplateRevisionsQuery,
  useLazyGetAdminTemplateRevisionsQuery,
  useGetAdminTemplateRevisionQuery,
  useLazyGetAdminTemplateRevisionQuery,
  // Types
  type AdminNoteTemplateInfo,
  type AdminNoteTemplateListResponse,
  type AdminCopyNoteTemplateResponse,
  type AdminConvertToOrgRequest,
  type AdminCopyNoteTemplateRequest,
  type AdminCreateOrgTemplateRequest,
  type AdminDeleteTemplateResponse,
  type NoteTemplateOwnerScope,
  type AdminGetNoteTemplateResponse,
  type AdminUpdateNoteTemplateResponse,
  type AdminUpdateNoteTemplateRequest,
  type AdminNoteTemplateSectionRequest,
  type NoteTemplate2,
  type NoteTemplateRevision2,
  type NoteTemplateSection2,
  // Provider templates types
  type EffectiveNoteTemplateSummary,
  type AdminProviderTemplateListResponse,
  // Revision history types
  type RevisionSummary,
  type RevisionDetail,
  type AdminListRevisionsResponse,
  type AdminGetRevisionResponse,
} from './noteTemplatesAdminApi';

// Custom hooks for operation tracking
import { useCallback, useEffect,useRef } from 'react';

export interface OperationProgress {
  operationId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: {
    imported_count?: number;
    failed_count?: number;
    skipped_count?: number;
    total_count?: number;
  };
  error?: string;
  completedAt?: string;
}

export interface UseImportOperationTrackingOptions {
  sessionId: string;
  onOperationComplete?: (operation: OperationProgress) => void;
  onOperationError?: (operation: OperationProgress) => void;
  pollingInterval?: number;
}

export const useImportOperationTracking = ({
  sessionId,
  onOperationComplete,
  onOperationError,
  pollingInterval = 2000,
}: UseImportOperationTrackingOptions) => {
  const activeOperations = useRef<Set<string>>(new Set());
  const pollingTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Get the operation status query hook
  const [getOperationStatus] = adminApi.useLazyGetImportOperationStatusQuery();

  const startTracking = useCallback((operationId: string) => {
    if (activeOperations.current.has(operationId)) {
      return; // Already tracking
    }

    activeOperations.current.add(operationId);

    const poll = async () => {
      try {
        const result = await getOperationStatus({
          sessionId,
          operationId,
        }).unwrap();

        const operation: OperationProgress = {
          operationId,
          status: result.status as OperationProgress['status'],
          progress: {
            imported_count: result.results_imported_count,
            failed_count: result.results_failed_count,
            skipped_count: result.results_skipped_count,
            total_count: result.progress_total_count,
          },
          error: result.error_details?.[0]?.message || undefined,
          completedAt: result.completed_at || undefined,
        };

        if (result.status === 'completed') {
          // Operation completed successfully
          activeOperations.current.delete(operationId);
          const timer = pollingTimers.current.get(operationId);
          if (timer) {
            clearTimeout(timer);
            pollingTimers.current.delete(operationId);
          }
          onOperationComplete?.(operation);
        } else if (result.status === 'failed') {
          // Operation failed
          activeOperations.current.delete(operationId);
          const timer = pollingTimers.current.get(operationId);
          if (timer) {
            clearTimeout(timer);
            pollingTimers.current.delete(operationId);
          }
          onOperationError?.(operation);
        } else {
          // Still processing, schedule next poll
          const timer = setTimeout(() => {
            void poll();
          }, pollingInterval);
          pollingTimers.current.set(operationId, timer);
        }
      } catch (error) {
        console.error('Error polling operation status:', error);
        // Continue polling on error - the operation might still be running
        const timer = setTimeout(() => {
          void poll();
        }, pollingInterval);
        pollingTimers.current.set(operationId, timer);
      }
    };

    // Start polling immediately
    poll();
  }, [sessionId, getOperationStatus, onOperationComplete, onOperationError, pollingInterval]);

  const stopTracking = useCallback((operationId: string) => {
    activeOperations.current.delete(operationId);
    const timer = pollingTimers.current.get(operationId);
    if (timer) {
      clearTimeout(timer);
      pollingTimers.current.delete(operationId);
    }
  }, []);

  const stopAllTracking = useCallback(() => {
    pollingTimers.current.forEach(timer => clearTimeout(timer));
    pollingTimers.current.clear();
    activeOperations.current.clear();
  }, []);

  const isTracking = useCallback((operationId: string) => {
    return activeOperations.current.has(operationId);
  }, []);

  const getActiveOperations = useCallback(() => {
    return Array.from(activeOperations.current);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllTracking();
    };
  }, [stopAllTracking]);

  return {
    startTracking,
    stopTracking,
    stopAllTracking,
    isTracking,
    getActiveOperations,
  };
};
